import { GenericTable } from "@shared/components/GenericTable";
import { Badge } from "@shared/ui/badge";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { AlertTriangle, Clock, Shield } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useOutstandingBills, useOutstandingBillsExport } from "../../hooks";
import { OutstandingBill, OutstandingBillsFilters } from "../../types";

const renderRisk = (value: string | undefined) => {
  if (!value) return <span>N/A</span>;
  if (value === "High") {
    return (
      <span
        style={{
          color: "#e53e3e",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> High
      </span>
    );
  }
  if (value === "Medium") {
    return (
      <span
        style={{
          color: "#d97706",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Clock size={16} style={{ color: "#d97706" }} /> Medium
      </span>
    );
  }
  if (value === "Low") {
    return (
      <span
        style={{
          color: "#2563eb",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Shield size={16} style={{ color: "#2563eb" }} /> Low
      </span>
    );
  }
  return value;
};

const formatCurrency = (
  currency: string,
  value: string | number | null | undefined,
) => {
  if (value === null || value === undefined || value === "") {
    return `${currency} NA`;
  }
  return `${currency} ${value}`;
};

// Define a table-specific type for the transformed data
interface OutstandingBillTableRow {
  id: string;
  accountNumber: string;
  remoteConsumerNumber: string;
  customerName: string;
  consumerStatus: string;
  totalOutstanding: string;
  thirtyDays: string;
  sixtyDays: string;
  ninetyDays: string;
  aboveNinetyDays: string;
  risk: string;
}

export const OutstandingCurrentList: React.FC = () => {
  const navigate = useNavigate();
  const currency = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // URL Params — single source of truth
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      searchData: currentSearch,
    }),
    [currentPage, currentSearch],
  );

  const { data } = useOutstandingBills({
    remoteUtilityId: Number(remoteUtilityId),
    page: queryParams.page,
    limit: PAGE_SIZE,
    searchData: queryParams.searchData,
  });

  const exportFilters: OutstandingBillsFilters = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      searchData: queryParams.searchData,
      // Note: No page and limit for export to get all data
    };
  }, [queryParams.searchData, remoteUtilityId]);

  const { exportCSV, isExporting } = useOutstandingBillsExport(exportFilters);

  const handleExport = () => {
    exportCSV();
  };

  const columns: TableColumn<OutstandingBillTableRow>[] = [
    {
      key: "accountNumber",
      header: "Consumer",
      type: "custom",
      render: (_, row) => (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">
            {row.customerName}
          </span>
          <span className="text-muted-foreground text-xs">
            {row.accountNumber}
          </span>
        </div>
      ),
    },
    {
      key: "consumerStatus",
      header: "Account Status",
      type: "custom",
      render: (value) => {
        const lowerValue = value?.toLowerCase() || "n/a";
        let colorClass = "bg-white text-gray-700 border-gray-300";
        if (lowerValue === "active") {
          colorClass = "bg-green-50 text-green-700 border-green-200";
        } else if (lowerValue === "inactive") {
          colorClass = "bg-red-50 text-red-700 border-red-200";
        }
        return (
          <Badge variant="outline" className={`capitalize ${colorClass}`}>
            {lowerValue}
          </Badge>
        );
      },
    },
    {
      key: "totalOutstanding",
      header: "Total outstanding",
      type: "custom",
      render: (value) => formatCurrency(currency, value),
    },
    {
      key: "thirtyDays",
      header: "< 30 days",
      type: "custom",
      render: (value) => formatCurrency(currency, value),
    },
    {
      key: "sixtyDays",
      header: "30-60 days",
      type: "custom",
      render: (value) => formatCurrency(currency, value),
    },
    {
      key: "ninetyDays",
      header: "60 - 90 days",
      type: "custom",
      render: (value) => formatCurrency(currency, value),
    },
    {
      key: "aboveNinetyDays",
      header: "90 days",
      type: "custom",
      render: (value) => formatCurrency(currency, value),
    },
    {
      key: "risk",
      header: "Risk",
      type: "custom",
      render: (value) => renderRisk(value || ""),
    },
  ];

  const actions: TableAction<OutstandingBillTableRow>[] = [
    {
      label: "View detail",
      onClick: (item) =>
        navigate(`/cx/outstanding/${item.remoteConsumerNumber}`),
    },
  ];

  // Transform API data to match the table structure
  const transformedData: OutstandingBillTableRow[] =
    data?.result?.map((item: OutstandingBill) => ({
      id: item.id?.toString() || item.remoteConsumerNumber,
      accountNumber: item.remoteConsumerNumber,
      remoteConsumerNumber: item.remoteConsumerNumber,
      customerName:
        item.fullName ||
        `${item.firstName || ""} ${item.lastName || ""}`.trim(),
      consumerStatus: item.consumerStatus,
      totalOutstanding: item.totalOutstanding,
      thirtyDays: item["30"],
      sixtyDays: item["60"],
      ninetyDays: item["90"],
      aboveNinetyDays: item[">90"],
      risk: item.risk,
    })) || [];

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    newParams.page = 1;
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  useEffect(() => {
    if (data) {
      const totalItems = data.count || 0;
      const totalPages = Math.ceil(totalItems / PAGE_SIZE);
      const startItem = (currentPage - 1) * PAGE_SIZE + 1;
      const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

      setPagination({
        currentPage: currentPage,
        totalPages,
        totalItems,
        itemsPerPage: PAGE_SIZE,
        startItem: totalItems > 0 ? startItem : 0,
        endItem: totalItems > 0 ? endItem : 0,
      });
    }
  }, [data, currentPage]);

  return (
    <GenericTable
      data={transformedData}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search by account number or customer name..."
      searchParamName="query"
      searchDebounceMs={300}
      onSearch={handleSearch}
      pagination={pagination}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      onExport={handleExport} // Add this line
      exportLoading={isExporting} // Add this line
    />
  );
};
